import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-number-input";
import { Button, Card, Input, Select, Upload } from "antd";
import {
   DocumentIcon,
   FileImport,
   MinusButtonWithColor,
   PlusIconBlue,
   RemoveCvIcon
} from "../../assets/icons";
import { Field } from "formik";
import { httpService } from "../../base/services/httpService.service";
import { useTranslation } from "../../i18n/LanguageContext";
const EditProfileHumanitarian = ({
   isEditMode,
   handleChange,
   values,
   nations,
   countryList,
   setFieldValue,
   handleOperatingCountries,
   errors,
   setSelectedCountry,
   touched,
   languageOptions
}) => {
   const { t } = useTranslation();
   const [selectedNationality, setSelectedNationality] = useState(values?.nationalityId);
   const [selectedCountryOfResidence, setSelectedCountryOfResidence] = useState(
      values?.residenceCountryId
   );
   const [selectedOperatingCountries, setSelectedOperatingCountries] = useState(
      values?.operatingCountriesIds
   );
   const [languages, setLanguages] = useState(values?.languages);
   const [languageLevelOptions, setLanguageLevelOptions] = useState([]);
   const [degreeOptions, setDegreeOptions] = useState([]);

   useEffect(() => {
      setSelectedCountry(values?.baseCountryId);
      setSelectedNationality(values?.nationalityId);
      setSelectedCountryOfResidence(values?.residenceCountryId);
      setSelectedOperatingCountries(values?.operatingCountriesIds);
   }, [values]);

   const getNationalityLabel = (id) => {
      const nationality = nations?.find((nation) => nation?.value === id);
      return nationality ? nationality?.label : "";
   };

   const getCountryOfResidenceName = (id) => {
      const selectedCountry = countryList?.find((country) => country?.value === id);
      return selectedCountry ? selectedCountry?.label : "";
   };

   const fields = [
      {
         sectionName: t("personal-information-title"),
         propertyName: t("name-label"),
         name: "name",
         propertyValue: values?.name,
         isEditMode: isEditMode,
         handleChange: handleChange,
         isRequired: true,
         fieldType: "Name"
      },
      {
         sectionName: "",
         propertyName: t("personal-statement-label"),
         name: "personalStatement",
         propertyValue: values?.personalStatement,
         isEditMode: isEditMode,
         handleChange: handleChange,
         isRequired: true,
         fieldType: "PersonalStatement"
      },
      {
         propertyName: t("nationality-label"),
         name: "nationalityId",
         propertyValue: isEditMode ? selectedNationality : getNationalityLabel(selectedNationality),
         isEditMode: isEditMode,
         handleChange: handleChange,
         fieldType: "selectNationality",
         options: nations,
         onSelectChange: (selectedOption) => {
            setFieldValue("nationalityId", selectedOption);
         },
         isRequired: true
      },
      {
         propertyName: t("country-of-residence-label"),
         name: "residenceCountryId",
         propertyValue: isEditMode
            ? selectedCountryOfResidence
            : getCountryOfResidenceName(selectedCountryOfResidence),
         isEditMode: isEditMode,
         handleChange: handleChange,
         fieldType: "selectResidence",
         options: countryList,
         onSelectChange: (selectedOption) => {
            setFieldValue("residenceCountryId", selectedOption);
         },
         isRequired: true
      },
      {
         propertyName: t("operating-countries-label"),
         name: "operatingCountriesIds",
         propertyValue: selectedOperatingCountries,
         isEditMode: isEditMode,
         handleChange: handleChange,
         fieldType: "selectOperatingCountries",
         options: countryList,
         onSelectChange: (selectedCountries) => {
            setFieldValue(
               "operatingCountriesIds",
               selectedCountries.map((country) => ({
                  value: country?.value,
                  label: country?.label
               }))
            );
            handleOperatingCountries(selectedCountries, values);
            setFieldValue("operatingCountriesIds", selectedCountries);
         },
         values: values?.operatingCountries,
         isRequired: true
      },
      {
         propertyName: t("linkedin-profile-label"),
         name: "linkedIn",
         propertyValue: values?.linkedIn,
         isEditMode: isEditMode,
         handleChange: handleChange,
         isRequired: false
      },
      {
         sectionName: "",
         propertyName: t("phone-number"),
         name: "phoneNumber",
         fieldType: "phone",
         propertyValue: values?.phoneNumber,
         isEditMode: isEditMode,
         onSelectChange: (value) => {
            setFieldValue("phoneNumber", value);
         },
         isRequired: false
      }
   ];

   const professionalDetailsFields = [
      {
         sectionName: t("professional-details-title"),
         propertyName: t("highest-degree-label"),
         name: "higestDegree",
         fieldType: "highestDegree",
         propertyValue: values?.higestDegree,
         isEditMode: isEditMode,
         handleChange: handleChange,
         onSelectChange: (value) => {
            setFieldValue("higestDegree", value);
         },
         isRequired: true
      },
      {
         sectionName: "",
         propertyName: t("educational-institution-label"),
         name: "educationalInstitution",
         propertyValue: values?.educationalInstitution,
         isEditMode: isEditMode,
         handleChange: handleChange,
         isRequired: true,
         fieldType: "educationalInstitution"
      },
      {
         sectionName: "",
         propertyName: t("fields-of-study-label"),
         name: "studyFields",
         propertyValue: values?.studyFields,
         isEditMode: isEditMode,
         handleChange: handleChange,
         isRequired: true,
         fieldType: "studyFields"
      },
      {
         sectionName: "",
         propertyName: t("areas-of-expertise-column"),
         name: "expertises",
         propertyValue: values?.expertises,
         isEditMode: isEditMode,
         handleChange: handleChange,
         isRequired: true,
         fieldType: "areasOfExpertise"
      },
      {
         sectionName: "",
         propertyName: t("languages-label"),
         name: "languages",
         propertyValue: values?.languages,

         isEditMode: isEditMode,
         handleChange: handleChange,
         isRequired: true,
         fieldType: "languages"
      },
      {
         sectionName: "",
         propertyName: isEditMode ? "" : t("cv-label"),
         name: "cv",
         propertyValue: values?.cv,
         isEditMode: isEditMode,
         handleChange: handleChange,
         isRequired: false,
         fieldType: "cv"
      },
      {
         sectionName: "",
         propertyName: isEditMode ? "" : t("certification-training-label"),
         name: "certificate",
         propertyValue: values?.certificate,
         isEditMode: isEditMode,
         handleChange: handleChange,
         isRequired: false,
         fieldType: "certificate"
      }
   ];

   const handleAddExpertise = (setFieldValue) => {
      setFieldValue("expertises", [
         ...values?.expertises,
         { areaName: "", areaExperienceYears: "" }
      ]);
   };

   const handleRemoveExpertise = (index, setFieldValue) => {
      const updatedExpertise = values?.expertises?.filter((_, i) => i !== index);
      setFieldValue("expertises", updatedExpertise);
   };

   const handleAddLanguage = (setFieldValue, values) => {
      const updatedLanguages = [...values?.languages, { languageId: "", level: "" }];
      setFieldValue("languages", updatedLanguages);
   };

   const handleRemoveLanguage = (index, setFieldValue) => {
      const updatedLanguages = [...values?.languages];
      updatedLanguages.splice(index, 1);
      setFieldValue("languages", updatedLanguages);
   };

   const handleLanguageChange = (index, field, selectedOption, setFieldValue) => {
      const updatedLanguages = [...values.languages];
      updatedLanguages[index] = {
         ...updatedLanguages[index],
         [field]: selectedOption
      };
      setFieldValue("languages", updatedLanguages);
   };

   useEffect(() => {
      httpService.get(`Accounts/highest-degree`, (data) => {
         const degreeData = Object.entries(data.data).map(([_, item]) => ({
            value: item.value,
            label: item.label
         }));
         setDegreeOptions(degreeData);
      });

      httpService.get(`/Accounts/language-level`, (data) => {
         const languageLevelData = Object.entries(data.data).map(([_, item]) => ({
            value: item.value,
            label: item.label
         }));
         setLanguageLevelOptions(languageLevelData);
      });
   }, []);

   return (
      <>
         <div
            className="detailsGrid"
            style={{
               border: "1.5px solid #DFE6FF",
               borderRadius: "16px",
               padding: "24px 32px",
               display: "grid",
               // gridTemplateColumns: "repeat(2, 1fr)",
               gap: "16px"
            }}
         >
            {fields?.map((section, index) => {
               const isFullWidth =
                  section.fieldType === "phone" ||
                  section.fieldType === "Name" ||
                  section.fieldType === "PersonalStatement";

               return (
                  <div
                     style={{
                        display: "flex",
                        flexDirection: "column",
                        // justifyContent: "flex-end",
                        gridColumn: isFullWidth ? "1 / -1" : "auto"
                     }}
                     // {...section}
                     key={index}
                  >
                     {section?.sectionName && (
                        <span
                           style={{
                              color: "#003DAA",
                              fontWeight: 500,
                              fontFamily: "poppins",
                              fontSize: "18px",
                              marginBottom: "8px"
                           }}
                        >
                           {section.sectionName}
                        </span>
                     )}
                     <div
                        className={section.sectionName ? "sectionSpace" : "noSectionSpace"}
                        style={{
                           display: "flex",
                           flexDirection: "column"
                        }}
                     >
                        {isEditMode ? (
                           <span
                              style={{
                                 color: "#3A3737",
                                 fontSize: "16px",
                                 marginBottom: "4px",
                                 fontWeight: 500
                              }}
                           >
                              {section.isRequired && <span style={{ color: "red" }}> *</span>}{" "}
                              {section.propertyName}
                           </span>
                        ) : (
                           <span
                              style={{ color: "#4158CF", fontSize: "16px", marginBottom: "4px" }}
                           >
                              {section.propertyName}
                           </span>
                        )}

                        {isEditMode ? (
                           <div>
                              {section.fieldType === "selectNationality" ? (
                                 <Select
                                    name={section?.name}
                                    value={section?.propertyValue}
                                    options={section?.options}
                                    showSearch
                                    filterOption={(input, option) =>
                                       option?.label?.toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={section?.onSelectChange}
                                    placeholder={t("select-nationality-placeholder")}
                                    style={{ width: "100%", marginBottom: "8px" }}
                                 />
                              ) : section.fieldType === "selectOperatingCountries" ? (
                                 <Select
                                    mode="multiple"
                                    name={section?.name}
                                    value={section?.propertyValue}
                                    options={section?.options}
                                    showSearch
                                    filterOption={(input, option) =>
                                       option?.label?.toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={section?.onSelectChange}
                                    placeholder={t("select-operating-countries-placeholder")}
                                    style={{ width: "100%", marginBottom: "8px" }}
                                 />
                              ) : section.fieldType === "selectResidence" ? (
                                 <Select
                                    name={section.name}
                                    value={section?.propertyValue}
                                    options={section.options}
                                    showSearch
                                    filterOption={(input, option) =>
                                       option?.label?.toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={section?.onSelectChange}
                                    placeholder={t("select-country-of-residence-placeholder")}
                                    style={{ width: "100%", marginBottom: "8px" }}
                                 />
                              ) : section.fieldType === "phone" ? (
                                 <div>
                                    <PhoneInput
                                       name="phoneNumber"
                                       value={section?.propertyValue}
                                       international
                                       defaultCountry="US"
                                       onChange={section.onSelectChange}
                                       className="custom-phone-input"
                                    />
                                 </div>
                              ) : (
                                 <Input
                                    value={section?.propertyValue}
                                    onChange={handleChange}
                                    name={section?.name}
                                    style={{
                                       color: "#555",
                                       fontFamily: "Lato",
                                       fontSize: "14px"
                                    }}
                                 />
                              )}
                           </div>
                        ) : Array.isArray(section.propertyValue) ? (
                           <div style={{ display: "flex", flexWrap: "wrap" }}>
                              {section?.propertyValue?.map((item, index) => (
                                 <span
                                    key={item.value}
                                    style={{
                                       color: "#3A3737",
                                       fontWeight: 600,
                                       fontFamily: "Lato",
                                       fontSize: "18px",
                                       marginRight:
                                          index < section?.propertyValue?.length - 1 ? "5px" : "0"
                                    }}
                                 >
                                    {item.label}
                                    {index < section?.propertyValue?.length - 1 ? ", " : ""}
                                 </span>
                              ))}
                           </div>
                        ) : (
                           <span
                              style={{
                                 color: "#3A3737",
                                 fontWeight: 600,
                                 fontFamily: "Lato",
                                 fontSize: "18px"
                              }}
                           >
                              {section.propertyValue}
                           </span>
                        )}
                        {errors[section.name] && (
                           <div className="text-danger">{errors[section.name]}</div>
                        )}
                     </div>
                  </div>
               );
               // <div key={index}>
               //    <Sections {...section} />
               //    {errors[section.name] && <div className="text-danger">{errors[section.name]}</div>}
               // </div>
            })}
         </div>
         <div
            className="detailsGrid"
            // className="detailsGrid"
            style={{
               border: "1.5px solid #DFE6FF",
               borderRadius: "16px",
               padding: "24px 32px",
               display: "grid",
               gridTemplateColumns: isEditMode ? "1fr" : "repeat(2, 1fr)",
               // gridTemplateColumns: "repeat(2, 1fr)",
               gap: "16px"
            }}
         >
            {professionalDetailsFields?.map((item, index) => {
               const isFullWidth =
                  item.fieldType === "languages" ||
                  (isEditMode && ["expertises", "cv", "certificate"].includes(item.fieldType));

               const isAlwaysHalfWidth = ["educationalInstitution", "highestDegree"].includes(
                  item.fieldType
               );

               const isHalfWidthInNonEditMode =
                  !isEditMode && ["cv", "certificate"].includes(item.fieldType);

               return (
                  <div
                     style={{
                        display: "flex",
                        flexDirection: "column",
                        gridColumn: isFullWidth
                           ? "1 / -1" // Full width
                           : isAlwaysHalfWidth
                           ? "span 1" // Always half-width for educationalInstitution & highestDegree
                           : isHalfWidthInNonEditMode
                           ? "1 / -1" // Half-width only in non-edit mode
                           : "span 1"
                     }}
                     // {...section}
                     key={index}
                  >
                     {item.sectionName && (
                        <span
                           style={{
                              color: "#003DAA",
                              fontWeight: 500,
                              fontFamily: "poppins",
                              fontSize: "18px",
                              marginBottom: "8px"
                           }}
                        >
                           {item.sectionName}
                        </span>
                     )}
                     <div
                        className={item.sectionName ? "sectionSpace" : "noSectionSpace"}
                        style={{
                           display: "flex",
                           flexDirection: "column"
                        }}
                     >
                        {isEditMode ? (
                           <span
                              style={{
                                 color: "#3A3737",
                                 fontSize: "16px",
                                 marginBottom: "4px",
                                 fontWeight: 500
                              }}
                           >
                              {item.isRequired && <span style={{ color: "red" }}> *</span>}{" "}
                              {item?.propertyName}
                           </span>
                        ) : (
                           <span
                              style={{ color: "#4158CF", fontSize: "16px", marginBottom: "4px" }}
                           >
                              {item?.propertyName}
                           </span>
                        )}

                        {isEditMode ? (
                           item.fieldType === "highestDegree" ? (
                              <>
                                 <Select
                                    className="basic-single"
                                    options={degreeOptions}
                                    placeholder={t("select-highest-degree-placeholder")}
                                    name="higestDegree"
                                    onChange={item?.onSelectChange}
                                    value={item?.propertyValue}
                                 />
                                 {errors.HigestDegree && touched.HigestDegree && (
                                    <div className="text-danger">{errors.HigestDegree}</div>
                                 )}
                              </>
                           ) : item.fieldType === "areasOfExpertise" ? (
                              <>
                                 <Card
                                    style={{
                                       borderRadius: "8px",
                                       border: "1px solid #CAD6FF",
                                       background: "#F9FBFF"
                                    }}
                                 >
                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                       {values?.expertises?.map((item, index) => (
                                          <div
                                             key={index}
                                             style={{
                                                display: "flex",
                                                justifyContent: "space-between"
                                             }}
                                          >
                                             {/* Area Name Input */}
                                             <div className="form-group" style={{ width: "40%" }}>
                                                <label
                                                   style={{
                                                      fontSize: "16px",
                                                      color: "#3A3737",
                                                      fontWeight: 500,
                                                      marginBottom: "10px"
                                                   }}
                                                >
                                                   {t("area-label")}
                                                </label>
                                                <Field
                                                   type="text"
                                                   placeholder={t("enter-area-placeholder")}
                                                   name={`expertises[${index}].areaName`}
                                                   className="form-control"
                                                />
                                                {errors.Expertises?.[index]?.areaName &&
                                                   touched.Expertises?.[index]?.areaName && (
                                                      <div className="text-danger">
                                                         {errors.expertises[index].areaName}
                                                      </div>
                                                   )}
                                             </div>

                                             {/* Experience Years Input */}
                                             <div className="form-group" style={{ width: "40%" }}>
                                                <label
                                                   style={{
                                                      fontSize: "16px",
                                                      color: "#3A3737",
                                                      fontWeight: 500,
                                                      marginBottom: "10px"
                                                   }}
                                                >
                                                   {t("years-of-experience-column")}
                                                </label>
                                                <Field
                                                   type="text"
                                                   placeholder="3"
                                                   name={`expertises.${index}.areaExperienceYears`}
                                                   className="form-control"
                                                   onKeyDown={(event) => {
                                                      const allowedKeys = [
                                                         "Backspace",
                                                         "Delete",
                                                         "ArrowLeft",
                                                         "ArrowRight"
                                                      ];
                                                      const isNumber = /^\d$/.test(event.key);
                                                      if (
                                                         !isNumber &&
                                                         !allowedKeys.includes(event.key)
                                                      ) {
                                                         event.preventDefault();
                                                      }
                                                   }}
                                                />
                                                {errors.expertises?.[index]?.areaExperienceYears &&
                                                   touched.expertises?.[index]
                                                      ?.areaExperienceYears && (
                                                      <div className="text-danger">
                                                         {
                                                            errors.expertises[index]
                                                               .areaExperienceYears
                                                         }
                                                      </div>
                                                   )}
                                             </div>

                                             {/* Remove Button */}
                                             <div style={{ paddingTop: "22px", cursor: "pointer" }}>
                                                <img
                                                   onClick={() =>
                                                      handleRemoveExpertise(index, setFieldValue)
                                                   }
                                                   src={MinusButtonWithColor}
                                                   alt="MinusButtonWithColor"
                                                />
                                             </div>
                                          </div>
                                       ))}

                                       {/* Add Expertise Button */}
                                       <div className="uploader-profile">
                                          <Button
                                             onClick={() => handleAddExpertise(setFieldValue)}
                                             type="dashed"
                                             style={{
                                                width: "100%",
                                                height: "70px",
                                                background: "#F1F6FF"
                                             }}
                                          >
                                             <div className="uploading-profile">
                                                <img src={PlusIconBlue} alt="avatar" />
                                                <div>{t("add-another-area-button")}</div>
                                             </div>
                                          </Button>
                                       </div>
                                    </div>
                                 </Card>
                              </>
                           ) : item.fieldType === "languages" ? (
                              <>
                                 <Card
                                    style={{
                                       borderRadius: "8px",
                                       border: "1px solid  #CAD6FF",
                                       background: "#F9FBFF"
                                    }}
                                 >
                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                       {values?.languages.map((language, index) => {
                                          return (
                                             <div
                                                key={index}
                                                style={{
                                                   display: "flex",
                                                   justifyContent: "space-between"
                                                }}
                                             >
                                                <div
                                                   className="form-group fv-plugins-icon-container"
                                                   style={{ width: "40%" }}
                                                >
                                                   <label
                                                      style={{
                                                         fontSize: "16px",
                                                         color: "#3A3737",
                                                         fontWeight: 500,
                                                         marginBottom: "10px"
                                                      }}
                                                   >
                                                      {t("language-label")}
                                                   </label>
                                                   <Select
                                                      name={`Languages[${index}].languageId`}
                                                      className="basic-single"
                                                      options={languageOptions}
                                                      placeholder={t("select-language-placeholder")}
                                                      showSearch
                                                      filterOption={(input, option) =>
                                                         option?.label
                                                            ?.toLowerCase()
                                                            .includes(input.toLowerCase())
                                                      }
                                                      onChange={(selectedOption) =>
                                                         handleLanguageChange(
                                                            index,
                                                            "languageId",
                                                            selectedOption,
                                                            setFieldValue
                                                         )
                                                      }
                                                      value={languageOptions?.find(
                                                         (option) =>
                                                            option.value === language.languageId
                                                      )}
                                                   />
                                                   {errors.Languages?.[index]?.languageId &&
                                                      touched.Languages?.[index]?.languageId && (
                                                         <div className="text-danger">
                                                            {errors.Languages[index].languageId}
                                                         </div>
                                                      )}
                                                </div>
                                                <div
                                                   className="form-group fv-plugins-icon-container"
                                                   style={{ width: "40%" }}
                                                >
                                                   <label
                                                      style={{
                                                         fontSize: "16px",
                                                         color: "#3A3737",
                                                         fontWeight: 500,
                                                         marginBottom: "10px"
                                                      }}
                                                   >
                                                      {t("level-label")}
                                                   </label>

                                                   <Select
                                                      className="basic-single"
                                                      options={languageLevelOptions}
                                                      placeholder={t("select-level-placeholder")}
                                                      // isSearchable
                                                      onChange={(selectedOption) =>
                                                         handleLanguageChange(
                                                            index,
                                                            "level",
                                                            selectedOption,
                                                            setFieldValue
                                                         )
                                                      }
                                                      value={languageLevelOptions.find(
                                                         (option) =>
                                                            option.value === language?.level // krahaso me .value
                                                      )}
                                                   />

                                                   {errors.Languages?.[index]?.level &&
                                                      touched.Languages?.[index]?.level && (
                                                         <div className="text-danger">
                                                            {errors.Languages[index].level}
                                                         </div>
                                                      )}
                                                </div>
                                                <div
                                                   style={{ paddingTop: "22px", cursor: "pointer" }}
                                                >
                                                   <img
                                                      onClick={() =>
                                                         handleRemoveLanguage(index, setFieldValue)
                                                      }
                                                      src={MinusButtonWithColor}
                                                      alt="MinusButtonWithColor"
                                                   />
                                                </div>
                                             </div>
                                          );
                                       })}
                                       <div className="uploader">
                                          <Button
                                             onClick={() =>
                                                handleAddLanguage(setFieldValue, values)
                                             }
                                             type="dashed"
                                             style={{
                                                width: "100%",
                                                height: "70px",
                                                background: "#F1F6FF"
                                             }}
                                          >
                                             <div className="uploading">
                                                <img src={PlusIconBlue} alt="avatar" />
                                                <div>{t("add-another-language-button")}</div>
                                             </div>
                                          </Button>
                                       </div>
                                    </div>
                                 </Card>
                                 <div style={{ margin: "40px 0 0 0" }}>
                                    <hr
                                       style={{
                                          height: "1px",
                                          backgroundColor: "#DFE6FF",
                                          border: "none",
                                          width: "100%",
                                          marginTop: "0",
                                          marginBottom: "0"
                                       }}
                                    />
                                 </div>
                              </>
                           ) : item?.fieldType === "cv" ? (
                              <div className="form-group fv-plugins-icon-container">
                                 <label
                                    style={{
                                       fontSize: "16px",
                                       color: "#3A3737",
                                       fontWeight: 500,
                                       marginBottom: "10px"
                                    }}
                                 >
                                    {t("upload-cv-label")}
                                 </label>
                                 <div className="upload-profile">
                                    {!values?.cv ? (
                                       <Upload
                                          accept=".pdf"
                                          beforeUpload={(file) => {
                                             setFieldValue("cv", file);
                                             return false;
                                          }}
                                          listType="picture-card"
                                          className="avatar-uploader-profile"
                                          showUploadList={false}
                                       >
                                          {
                                             <div className="uploading-profile">
                                                <img
                                                   style={{ width: "40px", height: "40px" }}
                                                   src={FileImport}
                                                   alt="avatar"
                                                />
                                                <div>
                                                   <div>
                                                      {t("drag-drop-file-here-prefix")}{" "}
                                                      <span>{t("click-to-select-file-text")}</span>
                                                   </div>
                                                   <div className="xlsx-file-profile">
                                                      {t("pdf-file-type-badge")}
                                                   </div>
                                                   <div className="max-size-file-profile">
                                                      {t("max-file-size-text")}
                                                   </div>
                                                </div>
                                             </div>
                                          }
                                       </Upload>
                                    ) : (
                                       <>
                                          <div
                                             className="d-flex justify-content-between align-items-center"
                                             style={{
                                                padding: "12px 16px",
                                                border: "1px solid #DFE6FF",
                                                borderRadius: "8px",
                                                backgroundColor: "#F9FAFF"
                                             }}
                                          >
                                             <div className="d-flex justify-content-start align-items-center">
                                                <div
                                                   style={{ padding: "8px", borderRadius: "50%" }}
                                                >
                                                   <img
                                                      src={DocumentIcon}
                                                      alt="cv"
                                                      width={20}
                                                      height={20}
                                                   />
                                                </div>
                                                <div
                                                   className="d-flex flex-column"
                                                   style={{ gap: "2px" }}
                                                >
                                                   <span
                                                      style={{
                                                         color: "#3A3737",
                                                         fontWeight: 500,
                                                         fontSize: "14px"
                                                      }}
                                                   >
                                                      {values?.cv instanceof File
                                                         ? values?.cv?.name
                                                         : values?.cv}
                                                   </span>
                                                   {/* <span
                                                      style={{
                                                         color: "#777",
                                                         fontWeight: 500,
                                                         fontSize: "14px"
                                                      }}
                                                   >
                                                      6 MB
                                                   </span> */}
                                                </div>
                                             </div>
                                             <div>
                                                <button
                                                   type="button "
                                                   onClick={() => setFieldValue("cv", null)}
                                                   style={{ border: "none", background: "none" }}
                                                >
                                                   <img
                                                      src={RemoveCvIcon}
                                                      alt="remove"
                                                      width={24}
                                                      height={24}
                                                   />
                                                </button>
                                             </div>
                                          </div>
                                       </>
                                    )}
                                 </div>
                                 {errors.cv && touched.cv && (
                                    <div className="text-danger">{errors.cv}</div>
                                 )}
                              </div>
                           ) : item.fieldType === "certificate" ? (
                              <div className="form-group fv-plugins-icon-container">
                                 <label
                                    style={{
                                       fontSize: "16px",
                                       color: "#3A3737",
                                       fontWeight: 500,
                                       marginBottom: "10px"
                                    }}
                                 >
                                    {t("upload-certificate-label")}
                                 </label>
                                 <div className="upload-profile">
                                    {!values?.certificate ? (
                                       <Upload
                                          accept=".pdf"
                                          beforeUpload={(file) => {
                                             setFieldValue("certificate", file);
                                             return false;
                                          }}
                                          listType="picture-card"
                                          className="avatar-uploader-profile"
                                          showUploadList={false}
                                       >
                                          <div className="uploading-profile">
                                             <img
                                                style={{ width: "40px", height: "40px" }}
                                                src={FileImport}
                                                alt="avatar"
                                             />
                                             <div>
                                                <div>
                                                   {t("drag-drop-file-here-prefix")}{" "}
                                                   <span>{t("click-to-select-file-text")}</span>
                                                </div>
                                                <div className="xlsx-file-profile">
                                                   {t("pdf-file-type-badge")}
                                                </div>
                                                <div className="max-size-file-profile">
                                                   {t("max-file-size-text")}
                                                </div>
                                             </div>
                                          </div>
                                       </Upload>
                                    ) : (
                                       <>
                                          <div
                                             className="d-flex justify-content-between align-items-center"
                                             style={{
                                                padding: "12px 16px",
                                                border: "1px solid #DFE6FF",
                                                borderRadius: "8px",
                                                backgroundColor: "#F9FAFF"
                                             }}
                                          >
                                             <div className="d-flex justify-content-start align-items-center">
                                                <div
                                                   style={{ padding: "8px", borderRadius: "50%" }}
                                                >
                                                   <img
                                                      src={DocumentIcon}
                                                      alt="certificate"
                                                      width={20}
                                                      height={20}
                                                   />
                                                </div>
                                                <div
                                                   className="d-flex flex-column"
                                                   style={{ gap: "2px" }}
                                                >
                                                   <span
                                                      style={{
                                                         color: "#3A3737",
                                                         fontWeight: 500,
                                                         fontSize: "14px"
                                                      }}
                                                   >
                                                      {values?.certificate instanceof File
                                                         ? values?.certificate?.name
                                                         : values?.certificate}
                                                   </span>
                                                   {/* <span
                                                      style={{
                                                         color: "#777",
                                                         fontWeight: 500,
                                                         fontSize: "14px"
                                                      }}
                                                   >
                                                      6 MB
                                                   </span> */}
                                                </div>
                                             </div>
                                             <div>
                                                <button
                                                   type="button "
                                                   onClick={() =>
                                                      setFieldValue("certificate", null)
                                                   }
                                                   style={{ border: "none", background: "none" }}
                                                >
                                                   <img
                                                      src={RemoveCvIcon}
                                                      alt="remove"
                                                      width={24}
                                                      height={24}
                                                   />
                                                </button>
                                             </div>
                                          </div>
                                       </>
                                    )}
                                 </div>
                                 {errors.certificate && touched.certificate && (
                                    <div className="text-danger">{errors.certificate}</div>
                                 )}
                              </div>
                           ) : (
                              <Input
                                 value={item?.propertyValue}
                                 onChange={handleChange}
                                 name={item?.name}
                                 style={{
                                    color: "#555",
                                    fontFamily: "Lato",
                                    fontSize: "14px"
                                 }}
                              />
                           )
                        ) : Array.isArray(item?.propertyValue) ? (
                           <div className="d-flex flex-wrap">
                              {item?.propertyValue?.map((subItem, index) => {
                                 let languageAndLevel = [];

                                 if (subItem.languageId) {
                                    const languageName = languageOptions?.find(
                                       (option) => option?.value === subItem?.languageId
                                    )?.label;

                                    const languageLevel = languageLevelOptions?.find(
                                       (option) => option?.value === subItem?.level
                                    )?.label;

                                    languageAndLevel.push(`${languageName} (${languageLevel})`);

                                    languages.forEach((language, index) => {
                                       const languageName = language?.name;
                                       const languageLevel = language?.level;

                                       if (index === languages?.length - 1) {
                                          languageAndLevel.push(
                                             `${languageName} (${languageLevel})`
                                          );
                                       } else {
                                          languageAndLevel.push(
                                             `${languageName} (${languageLevel})`
                                          );
                                       }
                                    });
                                    const languageDisplay = languageAndLevel.join(", ");
                                    return (
                                       <div
                                          key={`language-${index}`}
                                          className="d-flex align-items-center"
                                       >
                                          <span
                                             style={{
                                                color: "#555",
                                                fontWeight: 500,
                                                fontFamily: "Lato",
                                                fontSize: "14px"
                                             }}
                                          >
                                             {languageDisplay}
                                          </span>
                                       </div>
                                    );
                                 } else if (subItem?.areaName) {
                                    return (
                                       <div
                                          key={`expertise-${index}`}
                                          className="d-flex align-items-center"
                                          style={{
                                             padding: "3px 16px",
                                             border: "1px solid #777",
                                             borderRadius: "16px",
                                             background: "#F7F7F7",
                                             margin: "5px"
                                          }}
                                       >
                                          <span
                                             style={{
                                                color: "#555",
                                                fontWeight: 600,
                                                fontFamily: "Lato",
                                                fontSize: "14px"
                                             }}
                                          >
                                             {t("area-of-expertise-colon-label")}
                                          </span>
                                          &nbsp;
                                          <span
                                             style={{
                                                color: "#555",
                                                fontWeight: 500,
                                                fontFamily: "Lato",
                                                fontSize: "14px"
                                             }}
                                          >
                                             {subItem?.areaExperienceYears} {t("years-suffix-label")}
                                          </span>
                                       </div>
                                    );
                                 }
                                 // Fallback for other arrays
                                 return (
                                    <div
                                       key={`other-${index}`}
                                       className="d-flex align-items-center"
                                       style={{
                                          padding: "3px 16px",
                                          border: "1px solid #777",
                                          borderRadius: "16px",
                                          background: "#F7F7F7",
                                          margin: "5px"
                                       }}
                                    >
                                       <span
                                          style={{
                                             color: "#555",
                                             fontWeight: 500,
                                             fontFamily: "Lato",
                                             fontSize: "14px"
                                          }}
                                       >
                                          {subItem.label || JSON.stringify(subItem)}
                                       </span>
                                    </div>
                                 );
                              })}
                           </div>
                        ) : (
                           <span
                              style={{
                                 color: "#3A3737",
                                 fontWeight: 600,
                                 fontFamily: "Lato",
                                 fontSize: "18px",
                                 wordBreak: "break-word"
                              }}
                           >
                              {typeof item.propertyValue === "object"
                                 ? item.propertyValue?.label
                                 : item.propertyValue}
                           </span>
                        )}
                        {item.name && errors?.[item.name] && touched?.[item.name] && (
                           <div className="text-danger">{errors[item.name]}</div>
                        )}
                     </div>
                  </div>
               );
            })}
         </div>
      </>
   );
};

export default EditProfileHumanitarian;
